import { Tooltip } from "antd";
import { MouseEvent, ReactNode } from "react";

import { RowActionIcon } from "./AdminPage.styles";

interface IRowActionProps {
  /**
   * What the action does, in the words that used to be printed on the button.
   * It becomes both the tooltip and the button's accessible name, so write it
   * as a label ("Register eMED"), not a sentence.
   */
  label: string;
  /** The glyph. Sized by the button, so pass the icon bare. */
  icon: ReactNode;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  /** Removes, disconnects or deactivates — turns the hover red. */
  danger?: boolean;
}

/**
 * One action on a table row, as an icon with its label in a tooltip
 * (EN-5146 UI feedback).
 *
 * These were text buttons. With up to four per row and labels as long as
 * "Sync tenant IDs", the actions column was setting the width of the whole
 * table — on Patients it took roughly a third of the screen for two controls
 * that are the same two controls on every row.
 *
 * The label is not decoration: it is the `aria-label`, so the button still
 * announces itself, and antd shows the tooltip on keyboard focus as well as
 * hover. A glyph with no name would be unusable without a mouse.
 *
 * Every one of these sits in a row that is itself often clickable, so the
 * click is stopped from propagating here rather than in each of the forty
 * call sites that used to remember to do it.
 */
export function RowAction({
  label,
  icon,
  onClick,
  disabled,
  danger,
}: IRowActionProps) {
  return (
    <Tooltip title={label} placement="top" mouseEnterDelay={0.2}>
      <RowActionIcon
        type="button"
        aria-label={label}
        disabled={disabled}
        tone={danger ? "danger" : undefined}
        onClick={(event) => {
          event.stopPropagation();
          onClick(event);
        }}
      >
        {icon}
      </RowActionIcon>
    </Tooltip>
  );
}
